import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { api, apiError } from "../api/client";
import { IconLoader, IconAlert } from "../components/Icons";
import SearchableSelect from "../components/SearchableSelect";

interface CashOperation {
  id: string;
  client_id: string | null;
  branch_id: string;
  branch_name: string | null;
  employee_id: string | null;
  employee_name: string | null;
  type: string;
  amount: number;
  currency: string;
  reason: string | null;
  note: string | null;
  occurred_at: string;
  shift_number: string | null;
}

interface Branch {
  id: string;
  name: string;
}

const TYPE_KEYS = ["cash_in", "cash_out", "float_start", "float_end", "adjustment"];

const TYPE_BADGES: Record<string, string> = {
  cash_in: "badge badge-green",
  cash_out: "badge badge-red",
  float_start: "badge badge-yellow",
  float_end: "badge badge-yellow",
  adjustment: "badge badge-yellow",
};

function formatMoney(minor: number, currency = "CZK"): string {
  return new Intl.NumberFormat("cs-CZ", { style: "currency", currency, minimumFractionDigits: 0 }).format(minor / 100);
}

export default function CashOperations() {
  const { t } = useTranslation();
  const [ops, setOps] = useState<CashOperation[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterBranch, setFilterBranch] = useState("all");
  const [filterType, setFilterType] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const params = new URLSearchParams({ limit: "500" });
      if (filterBranch !== "all") params.set("branch_id", filterBranch);
      if (filterType !== "all") params.set("type", filterType);
      if (dateFrom) params.set("date_from", `${dateFrom}T00:00:00Z`);
      if (dateTo) params.set("date_to", `${dateTo}T23:59:59Z`);
      const [list, brs] = await Promise.all([
        api.get<CashOperation[]>(`/finance/cash-operations?${params}`).then((r) => r.data),
        api.get<Branch[]>("/branches?limit=500").then((r) => r.data).catch(() => []),
      ]);
      setOps(list);
      setBranches(brs);
    } catch (e) {
      setErr(apiError(e, t("cashOps.loadError")));
    } finally {
      setLoading(false);
    }
  }, [filterBranch, filterType, dateFrom, dateTo, t]);

  const typeLabel = (type: string) => t(`cashOps.type.${type}`, { defaultValue: type });

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: 80 }}>
        <IconLoader size={32} className="muted" />
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <h1>{t("cashOps.title")}</h1>
        <p>{t("cashOps.subtitle")}</p>
      </div>

      {err && (
        <div className="card" style={{ padding: "12px 16px", marginBottom: 20, background: "var(--red-dim)", color: "var(--red)", display: "flex", alignItems: "center", gap: 8, fontSize: 14 }}>
          <IconAlert size={16} /> {err}
        </div>
      )}

      <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
        <input
          type="date"
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
          style={{ width: 160 }}
        />
        <input
          type="date"
          value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
          style={{ width: 160 }}
        />
        <div style={{ width: 200 }}>
          <SearchableSelect
            value={filterBranch}
            onChange={setFilterBranch}
            options={[{ value: "all", label: t("dashboard.allBranches") }, ...branches.map((b) => ({ value: b.id, label: b.name }))]}
            placeholder={t("dashboard.allBranches")}
            searchPlaceholder={t("dashboard.searchBranch")}
          />
        </div>
        <div style={{ width: 200 }}>
          <SearchableSelect
            value={filterType}
            onChange={setFilterType}
            options={[
              { value: "all", label: t("cashOps.allTypes") },
              ...TYPE_KEYS.map((k) => ({ value: k, label: typeLabel(k) })),
            ]}
            placeholder={t("cashOps.allTypes")}
            searchPlaceholder={t("cashOps.searchType")}
          />
        </div>
      </div>

      <div className="card">
        {ops.length === 0 ? (
          <div style={{ color: "var(--muted)", padding: 20, textAlign: "center" }}>{t("cashOps.empty")}</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>{t("cashOps.colDate")}</th>
                <th>{t("cashOps.colBranch")}</th>
                <th>{t("cashOps.colEmployee")}</th>
                <th>{t("cashOps.colType")}</th>
                <th>{t("cashOps.colAmount")}</th>
                <th>{t("cashOps.colReason")}</th>
                <th>{t("cashOps.colShift")}</th>
              </tr>
            </thead>
            <tbody>
              {ops.map((op) => (
                <tr key={op.id}>
                  <td>{new Date(op.occurred_at).toLocaleString("cs-CZ")}</td>
                  <td>{op.branch_name || "—"}</td>
                  <td>{op.employee_name || "—"}</td>
                  <td>
                    <span className={TYPE_BADGES[op.type] || "badge badge-yellow"}>
                      {typeLabel(op.type)}
                    </span>
                  </td>
                  <td style={{ fontWeight: 600, color: op.amount < 0 ? "var(--red)" : undefined }}>
                    {formatMoney(op.amount, op.currency)}
                  </td>
                  <td>{op.reason || op.note || "—"}</td>
                  <td>{op.shift_number || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
